import { useEffect, useState, useCallback } from 'react';

interface VersionInfo {
  version: string;
  buildTime: number;
}

interface UseAppVersionReturn {
  updateAvailable: boolean;
  currentVersion: string | null;
  checkForUpdate: () => Promise<boolean>;
  applyUpdate: () => void;
}

// Store the initial version when app loads
let initialVersion: VersionInfo | null = null;

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/version.json', {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

export function useAppVersion(): UseAppVersionReturn {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);

  // Initialize version on first load
  useEffect(() => {
    const init = async () => {
      if (!initialVersion) {
        initialVersion = await fetchVersion();
      }
      setCurrentVersion(initialVersion?.version || null);
    };
    init();
  }, []);

  const checkForUpdate = useCallback(async (): Promise<boolean> => {
    if (!initialVersion) {
      initialVersion = await fetchVersion();
      return false;
    }

    const latestVersion = await fetchVersion();
    if (!latestVersion) return false;

    const hasUpdate = latestVersion.buildTime !== initialVersion.buildTime;
    setUpdateAvailable(hasUpdate);
    return hasUpdate;
  }, []);

  const applyUpdate = useCallback(() => {
    // Clear all caches and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    sessionStorage.removeItem('chunk_reload_attempted');
    window.location.reload();
  }, []);

  // Check for updates periodically (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      checkForUpdate();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkForUpdate]);

  return { updateAvailable, currentVersion, checkForUpdate, applyUpdate };
}

// Standalone function for use in lazyWithRetry
export async function checkVersionMismatch(): Promise<boolean> {
  if (!initialVersion) {
    initialVersion = await fetchVersion();
    return false;
  }
  
  const latestVersion = await fetchVersion();
  if (!latestVersion) return false;
  
  return latestVersion.buildTime !== initialVersion.buildTime;
}
